/* eslint-disable @typescript-eslint/no-explicit-any */
import { PopupChildrenRenderProps } from 'components/Popup';
import { useSetTrue, useSetFalse } from 'hooks/booleans';
import { useClickOutside } from 'hooks/useClickOutside';
import { useState, useRef, useMemo, useCallback } from 'react';
import { AddOption } from './components/AddOption';
import { DropdownMultiple, Dropdown } from './components/Dropdown';
import { SelectInput } from './components/SelectInput';
import * as S from './Select.styled';
import { SelectProps, OptionProps } from './Select.types';
import { CONTAINER_BLOCK_ID } from 'helpers/constants';
import { useDropdownPosition } from 'hooks/useDropdownPosition';
import { useSelect } from 'hooks/useSelect';

export function RawSelect<OptionValue>({
    input,
    meta,
    onChange,
    value: fieldValue,
    options: rawOptions,
    placeholder,
    label,
    multiple,
    onSearch,
    keepSearch,
    addButtonContent,
    addPopupContent,
    loader,
    getLabel,
    search = true,
    defaultOption,
    required,
    freeMode,
    queryKey,
    staleTimeMs,
    position,
    defaultOpened,
    hideMeta,
    hideInput,
    ...compareProps
}: SelectProps<OptionValue>) {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [freezed, setFreezed] = useState(false);
    const shouldClose = useRef(false);

    const {
        isOpen,
        setIsOpen,
        searchValue,
        setSearchValue,
        filteredOptions,
        value,
        valueWithOption,
        handleChange,
        setLoading
    } = useSelect<OptionValue>({
        input,
        value: fieldValue,
        options: rawOptions,
        onSearch,
        loader,
        getLabel,
        defaultOption,
        keepSearch,
        multiple,
        onChange: onChange as any,
        queryKey,
        staleTimeMs
    });

    const freeze = useSetTrue(setFreezed);
    const unfreeze = useSetFalse(setFreezed);
    const close = useSetFalse(setIsOpen);

    const onDropdownChange = useCallback(
        (newOptions: OptionProps<OptionValue> | OptionProps<OptionValue>[]) => {
            if (!multiple && !keepSearch) {
                shouldClose.current = true;
                close();
                setSearchValue('');
            }
            if (
                keepSearch &&
                !Array.isArray(newOptions) &&
                (newOptions.label || String(newOptions.value))
            ) {
                setSearchValue(newOptions.label || String(newOptions.value));
            }

            const newValue = Array.isArray(newOptions)
                ? newOptions.map((val) => val.value)
                : newOptions.value;

            const change = onChange?.(
                newValue as OptionValue & OptionValue[],
                newOptions as OptionProps<OptionValue> &
                    OptionProps<OptionValue>[]
            );

            if (change && change instanceof Promise) {
                setLoading(true);
                change.then(() => {
                    setLoading(false);
                });
            }

            input?.onChange?.(newValue);
        },
        [
            multiple,
            keepSearch,
            onChange,
            input,
            close,
            setSearchValue,
            setLoading
        ]
    );

    const onClickOutside = useCallback(() => {
        if (!freezed) {
            if (freeMode && searchValue && !value) {
                const newValue = searchValue as unknown as OptionValue;
                handleChange(newValue, { value: newValue, label: searchValue });
            }
            close();
            if (
                multiple &&
                !keepSearch &&
                (value as OptionValue[]).length > 0
            ) {
                setSearchValue('');
            }
        }
    }, [
        freezed,
        close,
        multiple,
        keepSearch,
        value,
        freeMode,
        searchValue,
        handleChange,
        setSearchValue
    ]);

    const containerRef = useClickOutside(onClickOutside, [dropdownRef], isOpen);

    const dropdownPosition = useDropdownPosition(
        containerRef,
        dropdownRef,
        isOpen && !position,
        CONTAINER_BLOCK_ID
    );

    const [preparedPlaceholder, fillPlaceholder] = useMemo(() => {
        const getValue = () => {
            if (Array.isArray(valueWithOption)) {
                return valueWithOption
                    .map(({ label, value }) => label || value)
                    .join(', ');
            } else {
                return valueWithOption
                    ? (valueWithOption.label ?? String(valueWithOption.value))
                    : null;
            }
        };

        const valuePlaceholder = getValue();

        return [
            valuePlaceholder ||
                placeholder ||
                (search ? 'Поиск...' : 'Не выбрано'),
            !!valuePlaceholder
        ];
    }, [placeholder, valueWithOption, search]);

    const addPopupContentGetter = useCallback(
        (props: PopupChildrenRenderProps) =>
            addPopupContent && addPopupContent(props, handleChange as any),
        [addPopupContent, handleChange]
    );

    const dropdownContent = useMemo(
        () => (
            <S.Dropdown
                $isOpen={isOpen}
                $isDefaultOpen={defaultOpened}
                $position={position ?? dropdownPosition}
                ref={dropdownRef}
            >
                {filteredOptions.length > 0 ? (
                    multiple ? (
                        <DropdownMultiple<OptionValue>
                            options={filteredOptions}
                            value={valueWithOption as any}
                            onChange={onDropdownChange}
                            {...compareProps}
                        />
                    ) : (
                        <Dropdown<OptionValue>
                            options={filteredOptions}
                            value={valueWithOption as any}
                            onChange={onDropdownChange}
                            {...compareProps}
                        />
                    )
                ) : (
                    <S.NoOptionsText>Нет данных</S.NoOptionsText>
                )}
                {addPopupContent && (
                    <AddOption
                        onOpenPopup={freeze}
                        onClosePopup={unfreeze}
                        popupContent={addPopupContentGetter}
                        label={addButtonContent}
                    />
                )}
            </S.Dropdown>
        ),
        [
            isOpen,
            defaultOpened,
            position,
            dropdownPosition,
            filteredOptions,
            multiple,
            valueWithOption,
            onDropdownChange,
            compareProps,
            addPopupContent,
            freeze,
            unfreeze,
            addPopupContentGetter,
            addButtonContent
        ]
    );

    return (
        <S.Container ref={containerRef}>
            {!hideInput && (
                <SelectInput
                    input={input}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    // loading={loading}
                    placeholder={preparedPlaceholder}
                    fillPlaceholder={fillPlaceholder}
                    label={label as string}
                    meta={meta}
                    search={search}
                    required={required}
                    value={value}
                    shouldClose={shouldClose}
                    rightContent={!defaultOpened ? dropdownContent : null}
                    hideMeta={hideMeta}
                />
            )}
            {defaultOpened && dropdownContent}
        </S.Container>
    );
}
